// src/components/pages/Caixa/index.js
import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from 'react';
import { Context } from '../../../context/UserContext';

import { useNavigateParams } from '../../../hooks/useNavigateParams';
import { MaterialReactTable } from 'material-react-table';

import { parseISO, format, isValid } from 'date-fns';

import { ThemeProvider } from '@mui/material';
/* tema customizado*/
import tableThemeCustom from '../../form/tableThemeCustom';

/* hooks */
import useFlashMessage from '../../../hooks/useFlashMessage';

//Import Material React Table Translations
import { useMRTState } from '../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import CustomDialog from '../../layout/CustomDialog';

import { Delete, Edit, Print, MonetizationOn, MoneyOff } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    IconButton,
    Tooltip,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import tratar from '../../../utils/retornaTratamentoURL';
import CrudsAPI from '../../../api/CRUDs';
import { identificarCaixasSis, consultarCaixaSis } from '../../../api/caixaSis';

/* api */
import * as FinanceiroAPI from '../../../api/financeiro';
import * as SituacaoAPI from '../../../api/situacao';

/* util */
import { relatorios } from '../../../api/relatorios';
import { obterPermissoesUsuario, printPdf, resolveEmpresaTabela } from '../../../utils/funcoes';

import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import {
    podeImprimir,
    podeIncluir,
    podeEditar,
    podeBaixar,
    podeCancelar,
    podeCancelarBaixa,
    podeConferir,
} from '../../../hooks/usePermisionMRT';

const Index = () => {
    /**
     * Detecta quando a tela está no formato mobile.
     * O helper usa essa informação para ajustar pinning
     * e largura das colunas de ações.
     */
    const themeMRT = useTheme();
    const isMobile = useMediaQuery(themeMRT.breakpoints.down('sm'));

    const [token] = useState(localStorage.getItem('token')) || '';
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const [dados, setDados] = useState([]);
    const [erroSis, setErroSis] = useState(null);
    const [optionsSituacao, setSituacao] = useState([]);

    const urlParams = new URLSearchParams(window.location.search);

    const pathname = '/Caixa/CreateOrEdit';
    const ul = urlParams.get('ul') || 4;
    const li = urlParams.get('li') || 58;
    const tipo = urlParams.get('tipo');
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const btnText = urlParams.get('btnText') || 'Cadastrar';

    const params = new URLSearchParams({
        ul: ul,
        li: li,
        tipo: tipo,
        operacao: operacaoUUID,
        uuid: uuid,
        btnText: btnText,
    });

    const { parametro } = useContext(Context);
    //obtem o array de empresas vinculadas ou coloca a empresaId no array
    const { pessoaId, empresaId, empresaIds } = getEmpresaScope();
    const empresaTabelaId = resolveEmpresaTabela(parametro, empresaId);

    const mrt = useMRTState({
        storageKey: 'caixa.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        setColumnFiltersMode,
        columnFilters,
        setColumnFilters,
        globalFilter,
        setGlobalFilter,
        sorting,
        setSorting,
        pagination,
        setPagination,
        isError,
        setIsError,
        isLoading,
        setIsLoading,
        isRefetching,
        setIsRefetching,
        rowCount,
        setRowCount,
    } = mrt;

    const limitarRegistros = !podeConferir();

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
        isMobile,
    });

    //dialog customizado
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [dialogFuncao, setDialogFuncao] = useState();
    //servi parar evitar duplicidade na transformação
    const [confirmLoading, setConfirmLoading] = useState(false);
    //Consultas no backend
    const consultaSituacao = async ({ empresaId, campo, campos, operacaoSigla }) => {
        const { data } = await SituacaoAPI.situacao({ empresaId, campo, campos, operacaoSigla });

        const mappedResult = data.map((item) => ({
            text: item.descricao,
            value: item.descricao,
        }));

        setSituacao(mappedResult);
    };

    /* boot padrão */
    useEffect(() => {
        consultaSituacao({ empresaId: empresaTabelaId, campo: 'caixa' });
    }, []);

    // 1. O useMemo memoriza o objeto baseado em valores primitivos ou strings
    const consultaParams = useMemo(
        () => ({
            start: pagination.pageIndex * pagination.pageSize,
            size: pagination.pageSize,
            filters: JSON.stringify(columnFilters ?? []),
            filtersMode: JSON.stringify(columnFilterFns ?? {}),
            globalFilter: globalFilter ?? '',
            sorting: JSON.stringify(sorting ?? []),
            limitarRegistros,
        }),
        // Se passarmos os objetos transformados em string no useMemo, evitamos falsos positivos de mudança
        [
            pagination.pageIndex,
            pagination.pageSize,
            JSON.stringify(columnFilters),
            JSON.stringify(columnFilterFns),
            globalFilter,
            JSON.stringify(sorting),
            limitarRegistros,
        ]
    );

    // 2. O useCallback agora depende APENAS do objeto memorizado e do tamanho dos dados
    const fetchData = useCallback(async () => {
        if (!dados.length) {
            setIsLoading(true);
        } else {
            setIsRefetching(true);
        }

        try {
            // Usando await para garantir que os passos rodem na ordem certa
            const response = await CrudsAPI.caixa.listarObj(consultaParams);
            const rows = response.data.data;
            setDados(rows.map((row) => ({ ...row, sisOrigem: null })));
            setErroSis(null);
            try {
                const sis = await identificarCaixasSis(rows.map((row) => row.uuid));
                setDados(rows.map((row) => ({ ...row, sisOrigem: sis.get(row.uuid) })));
            } catch (errorSis) {
                setErroSis(`${errorSis.message}${errorSis.status ? ` (HTTP ${errorSis.status})` : ''}`);
            }
            setRowCount(response.data.meta.totalRowCount);
            setIsError(false);
        } catch (error) {
            setIsError(true);
            console.error(error);
        } finally {
            // O finally garante que o loading mude independente de dar erro ou sucesso
            setIsLoading(false);
            setIsRefetching(false);
        }
    }, [dados.length, consultaParams]); // <- Muito mais limpo e seguro

    //funções do dialog
    const handleOpenDialog = ({ row, funcao }) => {
        if ((row.original || row).sisOrigem !== false) {
            setFlashMessage(
                'Consulte e processe a sessão pelo painel SIS. Edição, baixa e estorno tradicionais estão bloqueados.',
                'warning'
            );
            return;
        }
        setOpenDialog(true);

        setSelectedRow(row);
        setDialogFuncao(funcao);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setConfirmLoading(false); // Ativa loading
    };

    const handleConfirmDialog = async () => {
        setConfirmLoading(true); // Ativa loading
        // Verifique se há uma linha selecionada
        let msgType = 'success';
        try {
            const uuid = (selectedRow?.original || selectedRow)?.uuid;
            const atual = await consultarCaixaSis(uuid);
            if (atual.sis !== false) throw new Error('Sessão SIS: utilize o painel de conferência e consolidação.');
        } catch (e) {
            setFlashMessage(e.message, 'warning');
            setOpenDialog(false);
            setConfirmLoading(false);
            return;
        }
        if (dialogFuncao === 'deletar' && selectedRow) {
            try {
                const response = await CrudsAPI.caixa.deletar(selectedRow);

                setFlashMessage(response.data.message, msgType);

                // Após a exclusão, chame a função fetchData() novamente para atualizar as unidades
                fetchData();
            } catch (error) {
                msgType = 'error';
                console.error(error);
                setFlashMessage(error.response?.data?.message || error.message, msgType);
            }
        }
        if (dialogFuncao === 'estornar' && selectedRow) {
            await handlerCaixa({ uuid: selectedRow.uuid, btnText: 'Estornar' });
        }
        if (dialogFuncao === 'baixar' && selectedRow) {
            await handlerCaixa({
                uuid: selectedRow.uuid,
                btnText: 'Baixar',
                rows: selectedRow.original?.caixaLancamentos?.length,
            });
        }
        setOpenDialog(false);
        setConfirmLoading(false);
    };

    const handlerCaixa = async (obj) => {
        try {
            const result = await FinanceiroAPI.caixaBaixarOrEstornar(obj);

            // Substitui a mensagem após o resultado
            setFlashMessage(result.message, result.type);
        } catch (error) {
            setFlashMessage('Erro ao efetuar baixa ou estorno do caixa.', 'error');
            console.error(error);
        } finally {
            fetchData();
        }
    };
    useEffect(() => {
        if (!token) {
            return navigate('/Auth', { replace: true });
        }

        //funcao para setar localStorage
        localStorage.setItem(
            'pagination',
            JSON.stringify({
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
            })
        );
        localStorage.setItem('columnFilterFns', JSON.stringify(columnFilterFns));
        localStorage.setItem('columnFilters', JSON.stringify(columnFilters));
        localStorage.setItem('globalFilter', JSON.stringify(globalFilter));
        localStorage.setItem('sorting', JSON.stringify(sorting));

        //permissões do usuario
        obterPermissoesUsuario(pessoaId, empresaId, ul, li);

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting, fetchData]);

    const columns = useMemo(
        () => [
            {
                id: 'Caixa',
                header: 'Lançamentos dos caixas',
                columns: [
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'contador',
                        header: 'Número',
                    },
                    {
                        accessorFn: (row) => new Date(row),
                        accessorKey: 'data',
                        header: 'Data',
                        filterVariant: 'date',
                        sortingFn: 'date',
                        columnFilterModeOptions: [
                            'fuzzy',
                            'equals',
                            'between',
                            'betweenInclusive',
                            'greaterThanOrEqualTo',
                            'lessThanOrEqualTo',
                        ],
                        muiTableBodyCellProps: () => ({
                            align: 'center',
                        }),
                        Cell: ({ row }) => {
                            return (
                                <span>
                                    {row.original?.data
                                        ? isValid(parseISO(row.original?.data))
                                            ? format(parseISO(row.original?.data), 'dd/MM/yyyy')
                                            : null
                                        : null}
                                </span>
                            );
                        },
                    },
                    {
                        accessorKey: 'Situacao.descricao',
                        header: 'Situação',
                        filterFn: 'equals',
                        filterVariant: 'select',
                        filterSelectOptions: optionsSituacao,
                    },
                    {
                        accessorKey: 'conferido',
                        header: 'Conferido',
                        filterVariant: 'select', // Define o filtro como seletor
                        filterFn: 'equals', // Define o tipo de filtro como "igual"
                        filterSelectOptions: [
                            { label: 'Todos', value: '' }, // Opção padrão
                            { label: 'Sim', value: '1' }, // Ativo
                            { label: 'Não', value: '0' }, // Inativo
                        ],
                        muiTableBodyCellProps: ({ row, cell }) => ({
                            align: 'center', // Alinha a célula ao centro
                        }),
                        Cell: ({ row }) => {
                            const value = row.original.conferido;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                    {
                        accessorKey: 'baixado',
                        header: 'Baixado',
                        filterVariant: 'select', // Define o filtro como seletor
                        filterFn: 'equals', // Define o tipo de filtro como "igual"
                        filterSelectOptions: [
                            { label: 'Todos', value: '' }, // Opção padrão
                            { label: 'Sim', value: '1' }, // Ativo
                            { label: 'Não', value: '0' }, // Inativo
                        ],
                        muiTableBodyCellProps: ({ row, cell }) => ({
                            align: 'center', // Alinha a célula ao centro
                        }),
                        Cell: ({ row }) => {
                            const value = row.original.baixado;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                    {
                        accessorKey: 'integrado',
                        header: 'Integrado',
                        filterVariant: 'select', // Define o filtro como seletor
                        filterFn: 'equals', // Define o tipo de filtro como "igual"
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Sim', value: '1' },
                            { label: 'Não', value: '0' },
                        ],
                        muiTableBodyCellProps: ({ row, cell }) => ({
                            align: 'center', // Alinha a célula ao centro
                        }),
                        Cell: ({ row }) => {
                            const value = row.original.integrado;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                ],
            },

            //column definitions...
        ],
        [optionsSituacao] //getCommonEditTextFieldProps
    );

    /**
     * Regras comuns às ações do desktop e do mobile.
     * Recebe a LINHA DO MRT (row), não somente row.original.
     *
     * Mantém as mesmas permissões do arquivo original. Quando a origem
     * SIS ainda não foi identificada (null/undefined), baixa, estorno e
     * exclusão tradicionais continuam bloqueados.
     */
    const getCaixaActionPermissions = (row) => {
        const { baixado, conferido, sisOrigem } = row.original || {};
        const sis = sisOrigem === true;
        const bloquearTradicional = sisOrigem !== false;

        return {
            sis,
            isEditarDisabled: !podeEditar() || (baixado && !sis),
            isBaixarDisabled: bloquearTradicional || baixado || !podeBaixar() || !conferido,
            isEstornarDisabled: bloquearTradicional || !podeCancelarBaixa() || !baixado,
            isImprimirDisabled: !podeImprimir(),
            isDeletarDisabled: bloquearTradicional || !podeCancelar() || conferido,
        };
    };

    /**
     * Mantém a navegação existente. O getRowId da tabela usa row.uuid;
     * por isso row.id, recebido do MRT, é o UUID usado nesta rota.
     */
    const handleEditarCaixa = (row) => {
        params.set('uuid', row.id);
        params.set('btnText', 'Atualizar');
        navigate(pathname, params);
    };

    /** Impressão individual, compartilhada pelos ícones e pelo menu. */
    const handleImprimirCaixa = async (row) => {
        const response = await relatorios('caixa', 'caixa', { uuid: row.id });
        printPdf(response.data);
    };

    /* modo tema */
    const theme = tableThemeCustom();

    return (
        <div>
            <div>
                {/* Modal de Exclusão */}
                {openDialog && dialogFuncao === 'deletar' && (
                    <CustomDialog
                        title="Delete CAIXA!"
                        content="Você tem certeza?"
                        onClose={handleCloseDialog}
                        onConfirm={handleConfirmDialog}
                        loading={confirmLoading}
                    />
                )}
                {openDialog && dialogFuncao === 'estornar' && (
                    <CustomDialog
                        title="Deseja ESTORNAR o CAIXA!"
                        content="Você tem certeza?"
                        onClose={handleCloseDialog}
                        onConfirm={handleConfirmDialog}
                        loading={confirmLoading}
                    />
                )}
                {openDialog && dialogFuncao === 'baixar' && (
                    <CustomDialog
                        title="Deseja BAIXAR o CAIXA!"
                        content="Você tem certeza?"
                        onClose={handleCloseDialog}
                        onConfirm={handleConfirmDialog}
                        loading={confirmLoading}
                    />
                )}
            </div>
            {erroSis && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Não foi possível identificar as sessões SIS: {erroSis}. As ações de baixa, estorno e exclusão permanecem
                    bloqueadas até atualizar a consulta.
                </Alert>
            )}
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    // A edição acontece em outro formulário, não dentro do MRT.
                    enableEditing={false}
                    enableRowActions
                    // Ajuste local: preserva as outras colunas definidas no helper.
                    // Desktop: espaço para os cinco ícones compactos.
                    // Mobile: somente o botão do menu de três pontos.
                    displayColumnDefOptions={{
                        ...mrtDefault.displayColumnDefOptions,
                        'mrt-row-actions': {
                            ...mrtDefault.displayColumnDefOptions?.['mrt-row-actions'],
                            size: isMobile ? 45 : 200,
                            minSize: isMobile ? 45 : 200,
                            maxSize: isMobile ? 45 : 260,
                        },
                    }}
                    renderRowActions={
                        !isMobile
                            ? ({ row }) => {
                                  const {
                                      sis,
                                      isEditarDisabled,
                                      isBaixarDisabled,
                                      isEstornarDisabled,
                                      isImprimirDisabled,
                                      isDeletarDisabled,
                                  } = getCaixaActionPermissions(row);

                                  return (
                                      <Box
                                          sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              flexWrap: 'nowrap',
                                              gap: 0.25,
                                              width: 'max-content',
                                          }}
                                      >
                                          <Tooltip
                                              arrow
                                              placement="top"
                                              title={sis ? 'Consultar / conferir caixa SIS' : 'Editar'}
                                          >
                                              <span>
                                                  <IconButton
                                                      type="button"
                                                      size="small"
                                                      aria-label={sis ? 'Consultar / conferir caixa SIS' : 'Editar'}
                                                      onClick={(event) => {
                                                          event.preventDefault();
                                                          event.stopPropagation();
                                                          handleEditarCaixa(row);
                                                      }}
                                                      disabled={isEditarDisabled}
                                                  >
                                                      <Edit fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          <Tooltip arrow placement="top-start" title="Baixar lançamentos do caixa">
                                              <span>
                                                  <IconButton
                                                      type="button"
                                                      size="small"
                                                      color="error"
                                                      aria-label="Baixar lançamentos do caixa"
                                                      onClick={(event) => {
                                                          event.preventDefault();
                                                          event.stopPropagation();
                                                          handleOpenDialog({ row: row.original, funcao: 'baixar' });
                                                      }}
                                                      disabled={isBaixarDisabled}
                                                  >
                                                      <MonetizationOn fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          <Tooltip arrow placement="top-start" title="Estornar lançamentos do caixa">
                                              <span>
                                                  <IconButton
                                                      type="button"
                                                      size="small"
                                                      color="error"
                                                      aria-label="Estornar lançamentos do caixa"
                                                      onClick={(event) => {
                                                          event.preventDefault();
                                                          event.stopPropagation();
                                                          handleOpenDialog({ row: row.original, funcao: 'estornar' });
                                                      }}
                                                      disabled={isEstornarDisabled}
                                                  >
                                                      <MoneyOff fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          <Tooltip arrow placement="bottom" title="Imprimir">
                                              <span>
                                                  <IconButton
                                                      type="button"
                                                      size="small"
                                                      aria-label="Imprimir"
                                                      onClick={(event) => {
                                                          event.preventDefault();
                                                          event.stopPropagation();
                                                          handleImprimirCaixa(row);
                                                      }}
                                                      disabled={isImprimirDisabled}
                                                  >
                                                      <Print fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          <Tooltip arrow placement="right" title="Deletar">
                                              <span>
                                                  <IconButton
                                                      type="button"
                                                      size="small"
                                                      color="error"
                                                      aria-label="Deletar"
                                                      onClick={(event) => {
                                                          event.preventDefault();
                                                          event.stopPropagation();
                                                          handleOpenDialog({ row, funcao: 'deletar' });
                                                      }}
                                                      disabled={isDeletarDisabled}
                                                  >
                                                      <Delete fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                      </Box>
                                  );
                              }
                            : undefined
                    }
                    // No mobile não usamos renderRowActions: o MRT desenha o menu.
                    // Os handlers recebem os mesmos objetos usados no desktop.
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => {
                                  const {
                                      sis,
                                      isEditarDisabled,
                                      isBaixarDisabled,
                                      isEstornarDisabled,
                                      isImprimirDisabled,
                                      isDeletarDisabled,
                                  } = getCaixaActionPermissions(row);

                                  return [
                                      <MenuItem
                                          key="editar"
                                          disabled={isEditarDisabled}
                                          onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              closeMenu();
                                              handleEditarCaixa(row);
                                          }}
                                      >
                                          <ListItemIcon>
                                              <Edit fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText>{sis ? 'Consultar / conferir caixa SIS' : 'Editar'}</ListItemText>
                                      </MenuItem>,
                                      <MenuItem
                                          key="baixar"
                                          disabled={isBaixarDisabled}
                                          onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              closeMenu();
                                              handleOpenDialog({ row: row.original, funcao: 'baixar' });
                                          }}
                                      >
                                          <ListItemIcon>
                                              <MonetizationOn fontSize="small" color="error" />
                                          </ListItemIcon>
                                          <ListItemText>Baixar caixa</ListItemText>
                                      </MenuItem>,
                                      <MenuItem
                                          key="estornar"
                                          disabled={isEstornarDisabled}
                                          onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              closeMenu();
                                              handleOpenDialog({ row: row.original, funcao: 'estornar' });
                                          }}
                                      >
                                          <ListItemIcon>
                                              <MoneyOff fontSize="small" color="error" />
                                          </ListItemIcon>
                                          <ListItemText>Estornar baixa</ListItemText>
                                      </MenuItem>,
                                      <MenuItem
                                          key="imprimir"
                                          disabled={isImprimirDisabled}
                                          onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              closeMenu();
                                              handleImprimirCaixa(row);
                                          }}
                                      >
                                          <ListItemIcon>
                                              <Print fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText>Imprimir</ListItemText>
                                      </MenuItem>,
                                      <MenuItem
                                          key="deletar"
                                          disabled={isDeletarDisabled}
                                          onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              closeMenu();
                                              handleOpenDialog({ row, funcao: 'deletar' });
                                          }}
                                      >
                                          <ListItemIcon>
                                              <Delete fontSize="small" color="error" />
                                          </ListItemIcon>
                                          <ListItemText>Deletar</ListItemText>
                                      </MenuItem>,
                                  ];
                              }
                            : undefined
                    }
                    renderTopToolbarCustomActions={() => {
                        // Definição das condições de desativação
                        const isIncluirDisabled = !podeIncluir();
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                }}
                            >
                                <Tooltip arrow placement="top-end" title={`Novo Registro`}>
                                    <div>
                                        {' '}
                                        <Button
                                            color="secondary"
                                            onClick={() => {
                                                params.delete('uuid');
                                                params.set('btnText', 'Cadastrar');
                                                navigate(pathname, params);
                                            }}
                                            variant="contained"
                                            disabled={isIncluirDisabled}
                                        >
                                            Novo
                                        </Button>
                                    </div>
                                </Tooltip>
                            </div>
                        );
                    }}
                />
            </ThemeProvider>
        </div>
    );
};
export default Index;
